import { ArrowLeft, Icon, Printer, Search, Users } from "@norican/icons";
import { Alert, Avatar, Button, EmptyState, Input, Skeleton, Typography } from "@norican/react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { AppShell } from "@/components/app-shell";
import { CardView } from "@/features/card/card-view";
import { apiStatus, type CardSearchResult, useCard, useCardSearch } from "@/lib/api";

/** `/colleagues` — find a colleague and open their card. */
export function ColleaguesPage() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { data, isFetching, error } = useCardSearch(query);
  const results = data?.results ?? [];
  const active = query.trim().length >= 2;

  return (
    <AppShell>
      <div className="mx-auto flex max-w-2xl flex-col gap-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <Typography as="h1" variant="heading-2">
              Colleagues
            </Typography>
            <Typography variant="body-sm" tone="muted">
              Look up a colleague and save or share their contact details.
            </Typography>
          </div>
          <Button
            variant="outline"
            tone="neutral"
            size="sm"
            render={<Link to="/colleagues/print" />}
            startIcon={<Icon icon={Printer} size={16} />}
          >
            Print export
          </Button>
        </div>
        <Input
          size="lg"
          type="search"
          autoFocus
          placeholder="Search by name or email"
          aria-label="Search colleagues"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          startSlot={<Icon icon={Search} size={16} />}
        />
        {error && (
          <Alert tone="danger" title="Search is not available">
            {apiStatus(error) === 503
              ? "The directory is not reachable right now. Try again in a moment."
              : "Something went wrong while searching."}
          </Alert>
        )}
        {!active && !error && (
          <EmptyState
            size="sm"
            icon={<Icon icon={Users} size={24} />}
            title="Start typing a name"
            description="At least two letters of a first name, surname or email address."
          />
        )}
        {active && isFetching && results.length === 0 && (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
            <Skeleton className="h-14 w-full rounded-lg" />
          </div>
        )}
        {active && !isFetching && !error && results.length === 0 && (
          <EmptyState
            size="sm"
            title="No colleague found"
            description="Try another spelling or the email address."
          />
        )}
        {results.length > 0 && (
          <ul className="bg-bg border-border divide-border divide-y overflow-hidden rounded-lg border">
            {results.map((hit) => (
              <li key={hit.id}>
                <ResultRow hit={hit} onOpen={() => navigate(`/colleagues/${hit.id}`)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

interface ResultRowProps {
  hit: CardSearchResult;
  onOpen: () => void;
}

function ResultRow({ hit, onOpen }: ResultRowProps) {
  const initials = hit.display_name
    .split(" ")
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
  const detail = [hit.job_title, hit.company_name].filter(Boolean).join(" · ");
  return (
    <button
      type="button"
      onClick={onOpen}
      className="hover:bg-surface-hover focus-visible:bg-surface-hover flex w-full items-center gap-3 px-4 py-3 text-left focus-visible:outline-none"
    >
      <Avatar.Root size="md">
        <Avatar.Image src={`/api/card/${hit.id}/photo`} alt="" />
        <Avatar.Fallback>{initials}</Avatar.Fallback>
      </Avatar.Root>
      <div className="min-w-0 flex-1">
        <Typography variant="body" truncate>
          {hit.display_name}
        </Typography>
        {detail && (
          <Typography variant="body-sm" tone="muted" truncate>
            {detail}
          </Typography>
        )}
      </div>
    </button>
  );
}

/** `/colleagues/:id` — one colleague's card. */
export function ColleagueCardPage() {
  const { id } = useParams<{ id: string }>();
  const { data: card, isLoading, error } = useCard(id);

  return (
    <AppShell>
      <div className="mb-6">
        <Button
          variant="ghost"
          tone="neutral"
          size="sm"
          render={<Link to="/colleagues" />}
          startIcon={<Icon icon={ArrowLeft} size={16} />}
        >
          Back to search
        </Button>
      </div>
      {isLoading && <Skeleton className="aspect-[85/55] w-full max-w-[560px] rounded-xl" />}
      {error && (
        <Alert tone="danger" title="Card not available">
          {apiStatus(error) === 404
            ? "There is no card for this account."
            : "The directory is not reachable right now. Try again in a moment."}
        </Alert>
      )}
      {card && <CardView card={card} photoUrl={`/api/card/${card.id}/photo`} owner="colleague" />}
    </AppShell>
  );
}
